import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Clock, CheckCircle2, XCircle, History, User as UserIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { PageHeader } from '../../components/Shared';
import {
  adminListTimesheetCorrections, adminApproveCorrection, adminRejectCorrection,
} from '../../services/api';

const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
};

export default function TimesheetCorrections() {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminNote, setAdminNote] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setRows(await adminListTimesheetCorrections(tab)); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not load corrections'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]);

  const act = async (c, verb) => {
    setBusyId(c.id);
    try {
      const note = (adminNote[c.id] || '').trim();
      if (verb === 'approve') await adminApproveCorrection(c.id, { adminNote: note });
      else await adminRejectCorrection(c.id, { adminNote: note });
      toast.success(verb === 'approve' ? 'Correction applied' : 'Correction rejected');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const counts = useMemo(() => ({
    pending: rows.filter(r => r.status === 'pending').length,
  }), [rows]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="admin-timesheet-corrections">
      <PageHeader
        title="Timesheet Corrections"
        description="Engineer-requested fixes to clocked hours. Review, apply or reject."
      />

      <div className="flex items-center gap-2 border-b border-slate-200">
        {['pending', 'approved', 'rejected', 'all'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm border-b-2 transition-colors capitalize ${tab === t ? 'border-slate-900 text-slate-900 font-medium' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`tsc-tab-${t}`}>
            {t} {t === 'pending' && counts.pending ? `(${counts.pending})` : ''}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {loading && <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-10 text-center text-slate-500 text-sm bg-white border border-slate-200 rounded-lg">
            Nothing to review.
          </div>
        )}
        {rows.map((c, i) => {
          const e = c.entry || {};
          const pending = c.status === 'pending';
          return (
            <div key={c.id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`tsc-row-${i}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 inline-flex items-center gap-2">
                    <UserIcon className="h-4 w-4 text-slate-400" />{c.userName || c.userId}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Submitted {fmtTime(c.createdAt)}
                    {c.decidedAt && ` · ${c.status} by ${c.decidedByName || c.decidedBy} at ${fmtTime(c.decidedAt)}`}
                  </div>
                </div>
                <StatusBadge status={c.status} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mt-3 text-sm">
                <div className="bg-slate-50 rounded p-3 border border-slate-200">
                  <div className="text-xs font-medium text-slate-500 uppercase mb-1">Original</div>
                  <div><b>Date:</b> {e.date || '—'}</div>
                  <div><b>In:</b> {fmtTime(c.originalClockIn)}</div>
                  <div><b>Out:</b> {fmtTime(c.originalClockOut)}</div>
                  <div><b>Break:</b> {c.originalBreakMins ?? 0}m</div>
                  <div><b>Site:</b> {e.siteName || e.workOrderId || '—'}</div>
                </div>
                <div className="bg-blue-50 rounded p-3 border border-blue-200">
                  <div className="text-xs font-medium text-blue-700 uppercase mb-1">Requested</div>
                  <div><b>In:</b> {fmtTime(c.requestedClockIn)}</div>
                  <div><b>Out:</b> {fmtTime(c.requestedClockOut)}</div>
                  <div><b>Break:</b> {c.requestedBreakMins ?? 0}m</div>
                  <div className="italic mt-2 text-slate-700">“{c.reason}”</div>
                </div>
              </div>
              {c.adminNote && !pending && (
                <div className="mt-2 text-xs text-slate-500 italic">Admin note: “{c.adminNote}”</div>
              )}
              {pending && (
                <div className="mt-3 flex flex-wrap items-end gap-2">
                  <input type="text" placeholder="Optional note to engineer…"
                    className="flex-1 border rounded px-2 py-1.5 text-sm min-w-[200px]"
                    value={adminNote[c.id] || ''}
                    onChange={(ev) => setAdminNote({ ...adminNote, [c.id]: ev.target.value })}
                    data-testid={`tsc-note-${i}`} />
                  <Button size="sm" variant="outline" onClick={() => act(c, 'reject')} disabled={busyId === c.id}
                    data-testid={`tsc-reject-${i}`}>
                    <XCircle className="h-4 w-4 mr-1" />Reject
                  </Button>
                  <Button size="sm" onClick={() => act(c, 'approve')} disabled={busyId === c.id}
                    data-testid={`tsc-approve-${i}`}>
                    <CheckCircle2 className="h-4 w-4 mr-1" />Approve & apply
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-800', Icon: History },
    approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
    rejected: { label: 'Rejected', cls: 'bg-rose-100 text-rose-800', Icon: XCircle },
  }[status] || { label: status, cls: 'bg-slate-100 text-slate-800', Icon: History };
  const { label, cls, Icon } = map;
  return (
    <span className={`text-xs px-2 py-0.5 rounded inline-flex items-center gap-1 ${cls}`}>
      <Icon className="h-3 w-3" />{label}
    </span>
  );
}
